import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  StApiError,
  StUserMessageStatsQuery,
  StUserMessageStatsResult,
  stErrorMessage,
  stExportUserMessageStatsCsv,
  stUserMessageStats,
} from '../../../services/socialTradingService';
import { StEmpty, StIdCell, StNameIdCell, StScrollTable, stFormatDateTime, stText } from '../StListUi';
import {
  MESSAGE_STATS_DEFAULT_TIME_ZONE,
  MESSAGE_STATS_PERMISSION_RESOURCE,
  MESSAGE_STATS_TIME_ZONES,
} from './constants';
import {
  defaultMessageStatsRange,
  messageStatsEmptyState,
  messageStatsErrorKey,
  parseCsv,
  todayInZone,
  unguardCsvCell,
  validateMessageStatsRange,
} from './helpers';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * The upstream export is CSV; the workbook is built here.
 *
 * Every cell is written as text so a UID or an EVM address cannot be turned
 * into a number in scientific notation — the one exception is the message
 * count, which is worth having as a real number to sort on.
 */
async function csvToXlsxBlob(csv: string, sheetName: string): Promise<Blob> {
  // Loaded on demand: the bundle is large and only this button needs it.
  // eslint-disable-next-line import/extensions
  const ExcelJS = await import('exceljs/dist/exceljs.min.js');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);
  const rows = parseCsv(csv);
  // The exporter writes four metadata rows before the column header.
  const headerIndex = 4;

  rows.forEach((cells, index) => {
    const row = sheet.addRow(cells.map(unguardCsvCell));
    if (index === headerIndex) row.font = { bold: true };
    if (index > headerIndex) {
      const last = row.getCell(cells.length);
      if (/^\d+$/.test(String(last.value ?? ''))) last.value = Number(last.value);
    }
  });

  sheet.columns.forEach((column) => {
    column.width = 22;
  });

  return new Blob([await workbook.xlsx.writeBuffer()], { type: XLSX_MIME });
}

const emptyForm = (): StUserMessageStatsQuery => ({
  ...defaultMessageStatsRange(),
  timeZone: MESSAGE_STATS_DEFAULT_TIME_ZONE,
  roomId: '',
  userId: '',
});

/** Blank `roomId` / `userId` mean "all", and the BFF rejects an empty string. */
const toQuery = (form: StUserMessageStatsQuery): StUserMessageStatsQuery => ({
  startDate: form.startDate,
  endDate: form.endDate,
  timeZone: form.timeZone,
  ...(form.roomId?.trim() ? { roomId: form.roomId.trim() } : {}),
  ...(form.userId?.trim() ? { userId: form.userId.trim() } : {}),
});

export default function SocialTradingMessageStatsList() {
  const t = useTranslate();
  const notify = useNotify();
  const [form, setForm] = useState<StUserMessageStatsQuery>(emptyForm);
  const [result, setResult] = useState<StUserMessageStatsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Kept on screen next to the previous result, which deliberately stays put.
  const [error, setError] = useState<string | null>(null);

  const rangeError = validateMessageStatsRange(form);
  const maxDate = todayInZone(form.timeZone);
  const emptyState = messageStatsEmptyState(result);

  const setField = (key: keyof StUserMessageStatsQuery, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const runQuery = async () => {
    if (rangeError) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await stUserMessageStats(toQuery(form)));
    } catch (e: any) {
      // The previous result stays on screen; the alert says it is stale.
      setError(t(messageStatsErrorKey(e instanceof StApiError ? e.code : undefined)));
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    if (rangeError) return;
    setExporting(true);
    try {
      const { blob, filename } = await stExportUserMessageStatsCsv(toQuery(form));
      const xlsx = await csvToXlsxBlob(await blob.text(), t('module.socialTrading.messageStats'));
      const url = window.URL.createObjectURL(xlsx);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.replace(/\.csv$/i, '') + '.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      notify(
        e instanceof StApiError && e.code
          ? t(messageStatsErrorKey(e.code))
          : stErrorMessage(e),
        { type: 'error' },
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    // <PermissionGuard
    //   resource={MESSAGE_STATS_PERMISSION_RESOURCE}
    //   action="read"
    //   fallback={<ErrorPage403 />}
    // >
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.messageStats')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.messageStats') },
          ]}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('module.socialTrading.messageStatsHint')}
        </Typography>

        <Card sx={{ mt: 2, p: 2 }}>
          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="date"
              label={t('module.socialTrading.filterFrom')}
              value={form.startDate}
              onChange={(e) => setField('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: maxDate }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              size="small"
              type="date"
              label={t('module.socialTrading.filterTo')}
              value={form.endDate}
              onChange={(e) => setField('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: maxDate }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              select
              size="small"
              label={t('module.socialTrading.filterTimeZone')}
              value={form.timeZone}
              onChange={(e) => setField('timeZone', e.target.value)}
              sx={{ minWidth: 190 }}
            >
              {MESSAGE_STATS_TIME_ZONES.map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('module.socialTrading.colRoomId')}
              value={form.roomId}
              onChange={(e) => setField('roomId', e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              label={t('module.socialTrading.colUserId')}
              value={form.userId}
              onChange={(e) => setField('userId', e.target.value)}
              sx={{ minWidth: 200 }}
            />

            <Button
              variant="contained"
              onClick={runQuery}
              disabled={loading || Boolean(rangeError)}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {t('module.socialTrading.search')}
            </Button>

            {/* <PermissionGuard
              resource={MESSAGE_STATS_PERMISSION_RESOURCE}
              action="export"
              fallback={<></>}
              loadingFallback={<></>}
            > */}
              <Button
                variant="outlined"
                onClick={exportCsv}
                disabled={exporting || Boolean(rangeError)}
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              >
                {t('module.socialTrading.exportXlsx')}
              </Button>
            {/* </PermissionGuard> */}
          </Stack>

          {rangeError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(`module.socialTrading.messageStatsErrors.${rangeError}`)}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error} {result ? t('module.socialTrading.messageStatsStaleResult') : ''}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('module.socialTrading.messageStatsTimeZoneNote')}
          </Typography>
          {result && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('module.socialTrading.messageStatsDataAsOf', {
                value: result.dataAsOf ? stFormatDateTime(result.dataAsOf) : '—',
              })}
            </Typography>
          )}
        </Card>

        <Card sx={{ mt: 2, pb: 2 }}>
          <Box sx={{ p: 2 }}>
            {emptyState !== 'none' && (
              <Typography variant="body2" color="text.secondary">
                {t(
                  emptyState === 'noDataYet'
                    ? 'module.socialTrading.messageStatsNoDataYet'
                    : 'module.socialTrading.messageStatsNoResults',
                )}
              </Typography>
            )}
            {!result && (
              <Typography variant="body2" color="text.secondary">
                {t('module.socialTrading.messageStatsRunQuery')}
              </Typography>
            )}
          </Box>

          {result && result.rows.length > 0 && (
            <StScrollTable minWidth={900}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('module.socialTrading.colRoom')}</TableCell>
                  <TableCell>{t('module.socialTrading.colUser')}</TableCell>
                  <TableCell>{t('module.socialTrading.colEvmAddress')}</TableCell>
                  <TableCell align="right">
                    {t('module.socialTrading.colMessageCount')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((row) => (
                  <TableRow key={`${row.roomId}:${row.userId}`}>
                    <TableCell>
                      <StNameIdCell name={row.roomName} id={row.roomId} />
                    </TableCell>
                    <TableCell>
                      <StNameIdCell name={row.nickname} id={row.userId} />
                    </TableCell>
                    <TableCell>
                      {/* Empty means "not linked" OR a failed lookup — the backend
                          cannot tell them apart, so neither should this. */}
                      {row.evmAddress ? (
                        <StIdCell value={row.evmAddress} head={10} tail={8} />
                      ) : (
                        <StEmpty>{t('module.socialTrading.walletNotLinked')}</StEmpty>
                      )}
                    </TableCell>
                    <TableCell align="right">{stText(row.messageCount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StScrollTable>
          )}
        </Card>
      </DashboardContent>
    // </PermissionGuard>
  );
}
